import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useMutation } from "@tanstack/react-query";

import { changeTransactionPin } from "../../../services/UserService";
import { updateAuthUser } from "../../../store/actions/Auth/AuthActions";
import { showMutationError } from "../../../utils/showMutationError";
import PinInput from "../PinInput";

const emptyForm = { currentPin: "", newPin: "", newPinConfirmation: "" };

const ChangePinModal = ({ show, onHide }) => {
  const authUser = useSelector((state) => state.auth.authUser);
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(emptyForm);

  const close = () => {
    setFormData(emptyForm);
    onHide();
  };

  const handleInput = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const mutation = useMutation({
    mutationFn: changeTransactionPin,
    onSuccess: (response) => {
      toast.success(response.data?.message || "Transaction PIN updated successfully.");
      dispatch(updateAuthUser({ has_transaction_pin: true, transaction_pin_expiry_date: null }));
      close();
    },
    onError: (err) => showMutationError(err, "Could not change your transaction PIN. Please try again."),
  });

  const handleSubmit = () => {
    mutation.mutate({
      partnerId: authUser?.partner_id,
      userId: authUser?.id,
      ...formData,
    });
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change Transaction PIN</Modal.Title>
      </Modal.Header>
      <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Enter your current transaction PIN, then choose a new one. Use six digits, avoiding obvious sequences or repeats.
          </p>

          <div className="mb-3">
            <label className="form-label" htmlFor="currentPin">
              Current Transaction PIN
            </label>
            <PinInput
              placeholder="******"
              id="currentPin"
              value={formData.currentPin}
              onChange={handleInput}
            />
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="newPin">
              New Transaction PIN
            </label>
            <PinInput
              placeholder="******"
              id="newPin"
              value={formData.newPin}
              onChange={handleInput}
            />
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="newPinConfirmation">
              Confirm New Transaction PIN
            </label>
            <PinInput
              placeholder="******"
              id="newPinConfirmation"
              value={formData.newPinConfirmation}
              onChange={handleInput}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              !formData.currentPin ||
              !formData.newPin ||
              !formData.newPinConfirmation ||
              mutation.isPending
            }
          >
            {mutation.isPending && <Spinner animation="border" size="sm" className="me-2" />}
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ChangePinModal;
